import type { CSSProperties } from 'react';
import { css, cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';
import type { Status } from './provider/stepper.provider';

export const stepperLineWidth = '2px';
const topPosition = '24px';
const marginSpacing = theme.spacing(3);
const buttonBarBreakpoint = '550px';
const titleWidth = '550px';
export const stepperDuration = '0.5s';
export const stepperDelay = '0.5s';
export const activeStepperDelay = '0.75s, 0.5s';
export const inactiveStepperDuration = '1s';

const isExpanded = (stepStatus: Status) =>
  stepStatus === 'active' || stepStatus === 'currActive' || stepStatus === 'errorActive';

/* -------------------------------------------------------------------------- */
/* stepper container                                                          */
/* -------------------------------------------------------------------------- */

const stepperBase = css`
  max-width: var(--stepper-max-width, 700px);
  margin-left: ${theme.spacing(1)};
  margin-right: ${theme.spacing(1)};
  .step-container:last-child .step-line {
    display: none;
    height: 0%;
  }
  .step-container {
    position: relative;
  }
`;

const stepperHorizontal = css`
  position: unset;
  margin: ${theme.spacing(1)};
  .step-content {
    margin-left: 0;
    margin-top: ${theme.spacing(10)};
    margin-right: ${theme.spacing(3)};
  }
  .step-card {
    max-width: unset;
    width: 100%;
    margin-top: ${theme.spacing(3)};
    margin-left: 0;
    margin-right: ${theme.spacing(2)};
  }
  .stepper-horizontal-step:last-child .step-line-horizontal {
    display: none;
  }
`;

const stepperVerticalSingle = css`
  display: flex;

  .step-container {
    overflow: auto;
    width: 100%;
    &:empty {
      width: 0;
    }
  }

  .step-card {
    border: none;
    border-radius: 0;
    margin: 0;
    max-width: unset;
    width: 100%;

    .step-content-title,
    .step-content-svg {
      font-size: ${theme.fontSize(24)};
    }
  }

  .stepper-vertical {
    margin-left: ${theme.spacing(1)};
    min-width: 200px;
    height: var(--stepper-vertical-height);
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .step-num-vertical {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .stepper-vertical-step {
    height: var(--stepper-vertical-step-height);
    position: relative;
    width: 100%;
  }

  .stepper-vertical-step:last-child .step-line-vertical {
    display: none;
  }
`;

export const stepper = (horizontal = false, isVerticalSingleView = false) =>
  cx(stepperBase, horizontal && stepperHorizontal, isVerticalSingleView && stepperVerticalSingle);

/**
 * Runtime-dynamic values for the stepper container. Linaria is zero-runtime, so
 * `maxWidth` and the vertical-single-view heights (which depend on `numSteps`)
 * are exposed as CSS custom properties consumed by the static classes above.
 */
export const stepperVars = (numSteps = 6, maxWidth?: number) =>
  ({
    '--stepper-max-width': `${maxWidth || 700}px`,
    '--stepper-vertical-height': `${parseInt(theme.spacing(10)) * numSteps}px`,
    '--stepper-vertical-step-height': `${100 / numSteps}%`,
  } as CSSProperties);

/* -------------------------------------------------------------------------- */
/* step card                                                                  */
/* -------------------------------------------------------------------------- */

const stepCardBase = css`
  overflow-y: hidden;
  padding: ${theme.spacing(3)};
  border: 1px solid ${theme.colors.neutral20};
  border-radius: ${theme.borderRadius.small};
  padding-bottom: ${theme.spacing(3)};
  min-height: 82px;
  margin-bottom: ${marginSpacing};
  &:not(:first-child) {
    margin-left: ${theme.spacing(5)};
  }
  .step-content-title,
  .step-content-svg {
    font-size: ${theme.fontSize(20)};
    color: ${theme.colors.neutral20};
    font-weight: ${theme.font.weight.regular};
  }
  .step-button {
    width: fit-content;
    @media only screen and (max-width: ${buttonBarBreakpoint}) {
      .skip-step {
        margin: 0;
      }
      .clear-step {
        margin: ${theme.spacing(0, 2, 0, 0)};
      }
    }
  }
  .step-button-bar {
    position: relative;
    margin-top: ${theme.spacing(3)};
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
  }
`;

const stepCardCollapsed = css`
  .step-content {
    display: none;
  }
  .step-button-bar,
  .step-content {
    display: none;
  }
  .step-content-title {
    width: ${titleWidth};
    height: 32px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const stepCardExpanded = css`
  height: unset;
  .step-content-title,
  .step-content-svg {
    font-weight: ${theme.font.weight.bold};
    color: ${theme.colors.neutral90};
  }
  .step-title-container {
    margin-bottom: ${theme.spacing(3)};
  }
  .step-content {
    display: initial;
  }
  .step-button-bar {
    display: flex;
  }
`;

const stepCardErrorOutline = css`
  border: 1px solid ${theme.colors.status.critical};
  outline: 1px solid ${theme.colors.status.critical};
`;

const stepCardCompleted = css`
  .step-content-title,
  .step-content-svg {
    color: ${theme.colors.neutral90};
  }
`;

const stepCardCompletedClickable = css`
  background-color: ${theme.colors.neutral10};
  cursor: pointer;
  .step-content-title,
  .step-content-svg {
    font-weight: ${theme.font.weight.bold};
  }
`;

const stepCardError = css`
  border: 1px solid ${theme.colors.status.critical};
  outline: 1px solid ${theme.colors.status.critical};
  .step-content-title,
  .step-content-svg {
    color: ${theme.colors.status.critical};
  }
`;

export const stepCard = (stepStatus: Status, allowClicking: boolean) =>
  cx(
    stepCardBase,
    !isExpanded(stepStatus) && stepCardCollapsed,
    isExpanded(stepStatus) && stepCardExpanded,
    stepStatus === 'errorActive' && stepCardErrorOutline,
    stepStatus === 'completed' && stepCardCompleted,
    stepStatus === 'completed' && allowClicking && stepCardCompletedClickable,
    stepStatus === 'error' && stepCardError
  );

/* -------------------------------------------------------------------------- */
/* step content                                                               */
/* -------------------------------------------------------------------------- */

const stepContentBase = css`
  height: 82px;
  margin-left: ${theme.spacing(4)};
  .step-content-title,
  .step-content-svg {
    color: ${theme.colors.neutral20};
    font-weight: ${theme.font.weight.regular};
  }
`;

const stepContentExpanded = css`
  height: unset;
  .step-content-title,
  .step-content-svg {
    font-weight: ${theme.font.weight.bold};
    color: ${theme.colors.neutral90};
  }
`;

const stepContentCompleted = css`
  background-color: ${theme.colors.neutral10};
  .step-content-title,
  .step-content-svg {
    color: ${theme.colors.neutral90};
  }
`;

export const stepContent = (stepStatus: Status) =>
  cx(
    stepContentBase,
    isExpanded(stepStatus) && stepContentExpanded,
    stepStatus === 'completed' && stepContentCompleted
  );

/* -------------------------------------------------------------------------- */
/* step line (vertical default)                                               */
/* -------------------------------------------------------------------------- */

const stepLineBase = css`
  width: ${stepperLineWidth};
  position: absolute;
  top: ${topPosition};
  height: calc(100% + ${marginSpacing});
`;

const stepLinePrimary = css`
  background-color: ${theme.colors.primary50};
`;

const stepLineCritical = css`
  background-color: ${theme.colors.status.critical};
`;

const stepLineInactive = css`
  background-color: ${theme.colors.neutral40};
`;

export const stepLine = (stepStatus: Status) =>
  cx(
    stepLineBase,
    (stepStatus === 'completed' || stepStatus === 'active' || stepStatus === 'currActive') && stepLinePrimary,
    (stepStatus === 'errorActive' || stepStatus === 'error') && stepLineCritical,
    stepStatus === 'inactive' && stepLineInactive
  );

/* -------------------------------------------------------------------------- */
/* step number                                                                */
/* -------------------------------------------------------------------------- */

const stepNumBase = css`
  position: absolute;
  top: ${topPosition};
  background-color: ${theme.colors.white};
  transform: translateX(-50%);
  border: 2px solid ${theme.colors.neutral40};
  border-radius: 50%;
  width: 24px;
  height: 24px;
  p,
  h2 {
    line-height: 20px;
    font-size: ${theme.fontSize(16)};
    text-align: center;
    color: ${theme.colors.neutral40};
    margin: 0;
  }
`;

const stepNumInactiveTransition = css`
  transition-delay: 0s;
  transition-duration: ${inactiveStepperDuration};
  transition-property: background-color, border, width, height, padding, line-height, transform, margin-left;
  p,
  h2 {
    transition-delay: 0s;
    transition-duration: ${inactiveStepperDuration};
    transition-property: color, line-height, font-size;
  }
`;

const stepNumCompleted = css`
  color: ${theme.colors.primary50};
  background-color: ${theme.colors.white};
  border: 2px solid ${theme.colors.primary50};
  width: 24px;
  height: 24px;
  padding: ${stepperLineWidth};
  p,
  h2 {
    color: ${theme.colors.neutral40};
  }
`;

const stepNumActive = css`
  top: ${topPosition};
  transform: translateX(-50%);
  background-color: ${theme.colors.primary50};
  border: 2px solid ${theme.colors.primary50};
  width: 32px;
  height: 32px;
  padding: 2px;
  transition-delay: ${stepperDelay};
  transition-duration: ${stepperDuration};
  transition-property: background-color, border, width, height, padding, line-height, transform, margin-left;
  p,
  h2 {
    line-height: ${theme.spacing(3)};
    font-size: ${theme.fontSize(18)};
    color: ${theme.colors.white};
    font-weight: ${theme.font.weight.bold};
    transition-delay: ${activeStepperDelay};
    transition-duration: ${stepperDuration};
    transition-property: color, line-height;
  }
`;

const stepNumError = css`
  border: 2px solid ${theme.colors.status.critical};
  background-color: ${theme.colors.status.critical};
  p,
  h2 {
    color: ${theme.colors.white};
  }
`;

export const stepNum = (stepStatus: Status) =>
  cx(
    stepNumBase,
    (stepStatus === 'inactive' || stepStatus === 'error') && stepNumInactiveTransition,
    stepStatus === 'completed' && stepNumCompleted,
    isExpanded(stepStatus) && stepNumActive,
    (stepStatus === 'error' || stepStatus === 'errorActive') && stepNumError
  );

/* -------------------------------------------------------------------------- */
/* step line (vertical single view)                                           */
/* -------------------------------------------------------------------------- */

const stepLineVerticalBase = css`
  width: ${stepperLineWidth};
  position: absolute;
  top: 41px;
  height: calc(100% - 22px);
`;

const stepLineVerticalPrimary = css`
  background-color: ${theme.colors.primary50};
`;

const stepLineVerticalCritical = css`
  background-color: ${theme.colors.status.critical};
`;

const stepLineVerticalInactive = css`
  background-color: ${theme.colors.neutral40};
  height: 100%;
`;

const stepLineVerticalError = css`
  background-color: ${theme.colors.status.critical};
  height: 100%;
`;

export const stepLineVertical = (stepStatus: Status) =>
  cx(
    stepLineVerticalBase,
    (stepStatus === 'completed' ||
      stepStatus === 'lastActive' ||
      stepStatus === 'active' ||
      stepStatus === 'currActive') &&
      stepLineVerticalPrimary,
    stepStatus === 'errorActive' && stepLineVerticalCritical,
    stepStatus === 'inactive' && stepLineVerticalInactive,
    stepStatus === 'error' && stepLineVerticalError
  );

/* -------------------------------------------------------------------------- */
/* step line text (vertical single view)                                      */
/* -------------------------------------------------------------------------- */

const stepLineTextBase = css`
  padding-left: ${theme.spacing(3.5)};
  color: ${theme.colors.neutral20};
  position: absolute;
`;

const stepLineTextFinished = css`
  pointer-events: none;
`;

const stepLineTextClickable = css`
  color: ${theme.colors.neutral50};
  &:hover {
    cursor: pointer;
    color: ${theme.colors.primary50};
  }
`;

const stepLineTextActive = css`
  font-weight: ${theme.font.weight.bold};
  color: ${theme.colors.neutral90};
`;

const stepLineTextErrorActive = css`
  font-weight: ${theme.font.weight.bold};
  color: ${theme.colors.status.critical};
`;

const stepLineTextError = css`
  color: ${theme.colors.status.critical};
`;

export const stepLineTextStyles = (stepStatus: Status) =>
  cx(
    stepLineTextBase,
    stepStatus === 'finished' && stepLineTextFinished,
    (stepStatus === 'completed' || stepStatus === 'lastActive') && stepLineTextClickable,
    (stepStatus === 'active' || stepStatus === 'currActive') && stepLineTextActive,
    stepStatus === 'errorActive' && stepLineTextErrorActive,
    stepStatus === 'error' && stepLineTextError
  );
